import { User } from './../users/user.actions';
import { UserState } from './../users/user.state';
import { UserService } from './../services/user/user.service';
import { OrganizationService } from './../services/organization/organization.service';
import { TransactionService } from './../services/transactions/transaction.service';
import { RoleService } from './../services/role/role.service';
import { ApplicationStateModel } from './app.model';
import { Action, State, StateContext, Selector, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App } from './app.actions';
import { HttpErrorResponse } from '@angular/common/http';
import {
  catchError,
  distinctUntilChanged,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { ILoginInformation } from '../models/login-information';
import { ILoginResult } from '../models/login-result';
import { Observable, Subject, of } from 'rxjs';
import { getMsUntilExpiry, isTokenExpired } from '../core/jwt.util';

@State<ApplicationStateModel>({
  name: 'application',
  defaults: {
    isLoading: false,
    authStatus: 'checking',
    roles: undefined,
    allRoles: undefined,
    actions: undefined,
    token: undefined,
    transactionTypes: undefined,
    hasOrganization: false,
    organizations: undefined,
    currentOrganization: undefined,
  },
})
@Injectable()
export class ApplicationState {
  unSubscribeAll$ = new Subject<void>();
  constructor(
    private roleService: RoleService,
    private userService: UserService,
    private organizationService: OrganizationService,
    private store: Store,
    private router: Router
  ) {}

  @Selector()
  public static getRoles(state: ApplicationStateModel) {
    return state.roles;
  }

  @Selector()
  public static getAllRoles(state: ApplicationStateModel) {
    return state.allRoles;
  }

  @Selector()
  public static getTransactionTypes(state: ApplicationStateModel) {
    return state.transactionTypes;
  }

  @Selector()
  public static getOrganizations(state: ApplicationStateModel) {
    return state.organizations;
  }

  @Selector()
  public static getCurrentOrganization(state: ApplicationStateModel) {
    return state.currentOrganization;
  }

  @Selector()
  public static getToken(state: ApplicationStateModel) {
    return state.token;
  }

  @Selector()
  public static getAuthStatus(state: ApplicationStateModel) {
    return state.authStatus;
  }

  @Selector()
  public static isAuthenticated(state: ApplicationStateModel) {
    return state.authStatus === 'authenticated';
  }

  private rolesCache = new Map<string, Observable<any[]>>();

  @Action(App.Actions.GetRoles)
  public getOrganizationRoles({
    getState,
  }: StateContext<ApplicationStateModel>) {
    return getState().roles;
  }

  @Action(App.Actions.GetAllRoles)
  public getAllOrganizationRoles({
    getState,
  }: StateContext<ApplicationStateModel>) {
    return getState().allRoles;
  }

  @Selector()
  public static hasOrganizationValue(state: ApplicationStateModel) {
    return state.hasOrganization;
  }

  @Action(App.Actions.SetRoles)
  public setOrganizationRoles({
    patchState,
    getState,
  }: StateContext<ApplicationStateModel>) {
    // allRoles is undefined only before the first successful fetch.
    // After that it is an array (possibly empty), so we can use it as a
    // "loaded" flag.  This prevents directives and guards that check
    // `roles.length === 0` (a valid state when the user has no permissions)
    // from re-fetching on every render.
    if (getState().allRoles !== undefined) {
      return;
    }

    const orgId = getState().currentOrganization?.id ?? '__global__';

    if (this.rolesCache.has(orgId)) {
      return this.rolesCache
        .get(orgId)!
        .pipe(tap((roles) => this.patchRoles(patchState, getState, roles)));
    }

    const request$ = this.roleService.list().pipe(take(1), shareReplay(1));

    this.rolesCache.set(orgId, request$);

    return request$.pipe(
      tap(
        (roles) => this.patchRoles(patchState, getState, roles),
        () => patchState({ isLoading: false })
      )
    );
    // return this.roleService.list().pipe(
    //   take(1), // Only take the first result
    //   shareReplay(1), // Cache the result,
    //   tap(
    //     (roles) => {
    //       const orgRoles =
    //         getState().currentOrganization &&
    //         getState().currentOrganization.roles
    //           ? roles.filter((role) => {
    //               return getState().currentOrganization.roles.includes(
    //                 role.role
    //               );
    //             })
    //           : roles;

    //       patchState({
    //         isLoading: false,
    //         roles: orgRoles,
    //         allRoles: roles,
    //       });
    //     },
    //     (errorResponse) => {
    //       patchState({
    //         isLoading: false,
    //       });
    //     }
    //   )
    // );
  }
  private patchRoles(
    patchState: (partial: Partial<ApplicationStateModel>) => void,
    getState: () => ApplicationStateModel,
    roles: any[]
  ) {
    const currentOrg = getState().currentOrganization;
    const userRoles = currentOrg?.roles;

    // If no org is in state yet (startup race), don't mark allRoles as loaded.
    // SetCurrentOrganization will reset allRoles and re-dispatch SetRoles once
    // the org is confirmed, ensuring roles are filtered correctly.
    if (!currentOrg) {
      return;
    }

    const orgRoles =
      userRoles && userRoles.length > 0
        ? roles.filter((role) => userRoles.includes(role.role))
        : [];

    patchState({
      isLoading: false,
      roles: orgRoles,
      allRoles: roles,
    });
  }

  private sessionInit$: Observable<void> | undefined;

  @Action(App.Actions.InitializeSession)
  public initializeSession({
    patchState,
    getState,
    dispatch,
  }: StateContext<ApplicationStateModel>) {
    // Once resolved (authenticated or not), further dispatches are a no-op —
    // this lets every route guard evaluation safely dispatch this action
    // without re-checking the session on every navigation.
    if (getState().authStatus !== 'checking') {
      return;
    }

    // Concurrent guard evaluations during the initial "checking" window share
    // the same in-flight request instead of firing one each.
    if (this.sessionInit$) {
      return this.sessionInit$;
    }

    const token = localStorage.getItem('dt-token');

    // Fail fast on a missing, malformed, or already-expired token — no
    // need to round-trip to the backend just to be told the same thing.
    if (!token || isTokenExpired(token)) {
      localStorage.removeItem('dt-token');
      localStorage.removeItem('user');
      patchState({ authStatus: 'unauthenticated' });
      return;
    }

    patchState({ token });

    this.sessionInit$ = this.userService.getSession().pipe(
      take(1),
      switchMap((user) => {
        dispatch(new User.Actions.SetLoginInformation(user));
        dispatch(new App.Actions.SetOrganizations(user.organizations || []));
        return dispatch(new App.Actions.SetRoles());
      }),
      tap(() => {
        // Reload case: the expiry timer only lives in memory, so it must be
        // recreated here every time session state is restored from scratch.
        this.scheduleAutoLogout(token);
        patchState({ authStatus: 'authenticated' });
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          localStorage.removeItem('dt-token');
          localStorage.removeItem('user');
        }
        // Network/5xx: leave any existing localStorage token alone — a flaky
        // request shouldn't log someone out. Either way, resolve out of
        // "checking" so the app never hangs on a loading screen.
        patchState({ authStatus: 'unauthenticated' });
        return of(undefined);
      }),
      shareReplay(1)
    );

    return this.sessionInit$;
  }

  @Action(App.Actions.ClearSession)
  public clearSession({ patchState }: StateContext<ApplicationStateModel>) {
    this.sessionInit$ = undefined;
    this.rolesCache.clear();
    this.clearAutoLogoutTimer();
    // Centralized here so every caller (logout, HTTP interceptor on 401,
    // the auto-logout timer) can just dispatch ClearSession instead of each
    // duplicating its own localStorage cleanup.
    localStorage.removeItem('dt-token');
    localStorage.removeItem('user');
    patchState({
      authStatus: 'unauthenticated',
      token: undefined,
      roles: undefined,
      allRoles: undefined,
      currentOrganization: undefined,
      organizations: undefined,
      hasOrganization: false,
    });
  }

  private autoLogoutTimerId: ReturnType<typeof setTimeout> | undefined;

  /**
   * Schedules automatic logout at the token's `exp` claim while the user
   * stays on the app without triggering a route guard or API call. Replaces
   * any previously scheduled timer — only one may be pending at a time.
   */
  private scheduleAutoLogout(token: string): void {
    this.clearAutoLogoutTimer();

    const msUntilExpiry = getMsUntilExpiry(token);
    if (msUntilExpiry === null) {
      return;
    }

    this.autoLogoutTimerId = setTimeout(() => {
      this.store.dispatch(new App.Actions.ClearSession());
      // Unlike a 401-triggered logout (mid API call, user is about to see
      // something break anyway), this timer can fire while the user is
      // idle looking at a fully-rendered protected page — redirect so
      // they're not left staring at stale data under a cleared session.
      if (!this.router.url.startsWith('/authentication/page-login')) {
        this.router.navigate(['/authentication/page-login']);
      }
    }, msUntilExpiry);
  }

  private clearAutoLogoutTimer(): void {
    if (this.autoLogoutTimerId !== undefined) {
      clearTimeout(this.autoLogoutTimerId);
      this.autoLogoutTimerId = undefined;
    }
  }

  @Action(App.Actions.SetActions)
  public setActions({ patchState }: StateContext<ApplicationStateModel>) {
    return this.roleService.list().pipe(
      tap(
        (roles) => {
          patchState({
            isLoading: false,
            roles: roles,
          });
        },
        (errorResponse) => {
          patchState({
            isLoading: false,
          });
        }
      )
    );
  }

  @Action(App.Actions.GetTransactionTypes)
  public getTransactionTypes({
    patchState,
  }: StateContext<ApplicationStateModel>) {
    return this.organizationService.getTransactionTypes().pipe(
      tap(
        (types) => {
          patchState({
            isLoading: false,
            transactionTypes: types,
          });
        },
        (errorResponse) => {
          patchState({
            isLoading: false,
          });
        }
      )
    );
  }

  @Action(App.Actions.SetToken)
  public setToken(
    { dispatch, patchState }: StateContext<ApplicationStateModel>,
    action: App.Actions.SetToken
  ) {
    // A freshly issued token replaces any timer left over from a prior one.
    this.scheduleAutoLogout(action.token);
    // The login flow is the only caller of SetToken, and it already has a
    // freshly-issued token plus the full user payload in hand — mark the
    // session authenticated directly rather than leaving authStatus stuck at
    // whatever NoAuthGuard's InitializeSession resolved it to (typically
    // 'unauthenticated', since the login page had no token). Without this,
    // AuthGuard reads that stale status on the very next navigation and
    // bounces the user straight back to login instead of the return URL.
    this.sessionInit$ = undefined;
    patchState({
      token: action.token,
      authStatus: 'authenticated',
      isLoading: false,
    });
  }

  @Action(App.Actions.SetOrganizations)
  public setOrganizations(
    { patchState, getState }: StateContext<ApplicationStateModel>,
    action: App.Actions.SetOrganizations
  ) {
    let organization = getState().currentOrganization;

    if (!organization) {
      // UserState.currentOrganization is already resolved against the
      // member's preferences.defaultOrganization (see
      // UserState.setLoginInformation) — use it here instead of blindly
      // taking organizations[0], which ignores the user's actual default.
      const defaultOrg = this.store.selectSnapshot(UserState.getCurrentOrganization);
      const defaultOrgId = this.organizationId(defaultOrg);

      organization =
        (defaultOrgId &&
          action.organizations.find(
            (org) => this.organizationId(org) === defaultOrgId
          )) ||
        action.organizations[0];
    }

    patchState({
      currentOrganization: organization,
      organizations: action.organizations,
      hasOrganization: action.organizations && action.organizations.length > 0,
    });
  }

  /**
   * Normalizes the identifying id off an organization entry, regardless of
   * whether it's shaped as a plain string id or a populated org sub-document
   * (both appear across this app's various organizations[] sources).
   */
  private organizationId(org: any): string | undefined {
    if (!org) return undefined;
    const raw = org.id ?? org.organizationId;
    const normalized = raw && typeof raw === 'object' ? raw._id ?? raw.id : raw;
    return normalized?.toString();
  }

  @Action(App.Actions.SetCurrentOrganization)
  public setCurrentOrganization(
    { dispatch, patchState }: StateContext<ApplicationStateModel>,
    action: App.Actions.SetCurrentOrganization
  ) {
    if (!action.organization) return;
    this.userService
      .setCurrentOrganization(action.organization.id)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe({
        next: (result: ILoginResult) => {
          localStorage.setItem('dt-token', result.token);
          localStorage.setItem('user', JSON.stringify(result.user));
          localStorage.setItem(
            'organization',
            JSON.stringify(action.organization)
          );

          // Reset allRoles so the early-return guard in SetRoles allows a
          // re-fetch filtered for this org. Without this, roles fetched before
          // currentOrganization was set in state (race on startup) would stick.
          patchState({
            token: result.token,
            currentOrganization: action.organization,
            allRoles: undefined,
            roles: undefined,
          });

          // Notify UserService subscribers (e.g. TransactionsComponent) once,
          // here after the API responds, instead of immediately in the caller.
          this.userService.currentOrganization$.next(action.organization as any);

          this.rolesCache.clear();
          dispatch(new App.Actions.SetRoles());
        },
        error: (errorResponse) => {
          patchState({
            isLoading: false,
          });
        },
      });
  }

  @Action(App.Actions.GetToken)
  public getToken({ getState }: StateContext<ApplicationStateModel>) {
    return getState().token;
  }

  @Action(App.Actions.SetHasOrganization)
  public setHasOrganization(
    { dispatch, patchState }: StateContext<ApplicationStateModel>,
    action: App.Actions.SetHasOrganization
  ) {
    patchState({
      hasOrganization: action.hasOrganization,
    });

    dispatch(new App.Actions.GetHasOrganization());
  }

  @Action(App.Actions.GetHasOrganization)
  public getHasOrganization({ getState }: StateContext<ApplicationStateModel>) {
    return getState().hasOrganization;
  }
  ngOnDestroy(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  }
}
